// Feed UI Component
import { getUserById, getPostById, repostPost } from '../db.js';
import { getVerse, getVerses } from '../bible.js';
import { getUserLanguage } from '../db.js';
import { getCurrentUser } from '../auth.js';
import { showComments } from './compose.js';
import { ReactionsBar } from './reactionsBar.js';
import { CommentsModal } from './commentsModal.js';
import { db } from '../firebase.js';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';

let feedContainer = null;
let isLoading = false;
let unsubscribeFeed = null;
let verseObserver = null;
let postsPerPage = 5;
let currentPostsCount = 0;
let allPosts = [];
let scrollObserver = null;

// Intersection Observer for lazy loading verses
const createVerseObserver = () => {
    return new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const verseContainer = entry.target;
                if (verseContainer.dataset.loaded !== 'true') {
                    loadVerseForArticle(verseContainer);
                    if (verseObserver) {
                        verseObserver.unobserve(entry.target);
                    }
                }
            }
        });
    }, { rootMargin: '100px' });
};

/**
 * Initialize feed
 */
export function initFeed(container) {
    feedContainer = container;
    if (!verseObserver) {
        verseObserver = createVerseObserver();
    }
    loadFeed();
    setupInfiniteScroll();
}

/**
 * Setup infinite scroll listener
 */
function setupInfiniteScroll() {
    scrollObserver = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && allPosts.length > currentPostsCount && !isLoading) {
            loadMorePosts();
        }
    }, { threshold: 0.1 });
}

/**
 * Load and display feed with real-time updates
 */
export async function loadFeed() {
    if (!feedContainer || isLoading) return;

    isLoading = true;
    feedContainer.innerHTML = '<div class="loading">⏳ Chargement des posts...</div>';

    try {
        // Unsubscribe from previous listener if exists
        if (unsubscribeFeed) {
            unsubscribeFeed();
        }

        const postsRef = collection(db, 'posts');
        const q = query(postsRef, orderBy('createdAt', 'desc'), limit(100));

        // Set up real-time listener
        unsubscribeFeed = onSnapshot(q, async (snapshot) => {
            if (snapshot.empty) {
                feedContainer.innerHTML = `
                    <div class="empty-state">
                        <div class="empty-icon">📖</div>
                        <h3>Aucun post pour le moment</h3>
                        <p>Soyez le premier à partager une réflexion biblique !</p>
                    </div>
                `;
                isLoading = false;
                return;
            }

            // Process posts
            const posts = [];
            for (const docSnap of snapshot.docs) {
                const postData = { id: docSnap.id, ...docSnap.data() };

                // Initialize reactions if they don't exist
                if (!postData.reactions) {
                    postData.reactions = {
                        heart: [],
                        amen: [],
                        thoughtful: [],
                        encouraging: [],
                        inspiring: []
                    };
                }

                // Fetch author info
                try {
                    postData.author = await getUserById(postData.authorId);
                } catch (error) {
                    console.error('Error fetching author:', error);
                    postData.author = {
                        displayName: 'Utilisateur inconnu',
                        photoURL: ''
                    };
                }

                posts.push(postData);
            }

            // Clear and rebuild feed
            feedContainer.innerHTML = '';
            allPosts = posts;
            currentPostsCount = 0;

            // Display first batch (5 posts)
            const initialPosts = posts.slice(0, postsPerPage);
            for (const post of initialPosts) {
                const postElement = await createPostElement(post);
                feedContainer.appendChild(postElement);
            }
            currentPostsCount = initialPosts.length;

            // Add scroll trigger for loading more posts
            if (posts.length > postsPerPage && scrollObserver) {
                const sentinel = document.createElement('div');
                sentinel.className = 'scroll-sentinel';
                sentinel.style.height = '1px';
                feedContainer.appendChild(sentinel);
                scrollObserver.observe(sentinel);
            }

            isLoading = false;
            console.log(`✅ Feed mis à jour avec ${posts.length} posts`);
        }, (error) => {
            console.error('Error loading feed:', error);
            feedContainer.innerHTML = `
                <div class="error-state">
                    <div class="error-icon">⚠️</div>
                    <h3>Erreur de chargement</h3>
                    <p>${error.message}</p>
                    <button onclick="location.reload()">Réessayer</button>
                </div>
            `;
            isLoading = false;
        });
    } catch (error) {
        console.error('Error initializing feed:', error);
        feedContainer.innerHTML = `
            <div class="error-state">
                <div class="error-icon">⚠️</div>
                <h3>Erreur d'initialisation</h3>
                <p>${error.message}</p>
                <button onclick="location.reload()">Réessayer</button>
            </div>
        `;
        isLoading = false;
    }
}

/**
 * Cleanup feed listeners
 */
export function cleanupFeed() {
    if (unsubscribeFeed) {
        unsubscribeFeed();
        unsubscribeFeed = null;
    }
    if (verseObserver) {
        verseObserver.disconnect();
    }
    if (scrollObserver) {
        scrollObserver.disconnect();
        scrollObserver = null;
    }
}

/**
 * Load more posts on scroll
 */
async function loadMorePosts() {
    if (isLoading || currentPostsCount >= allPosts.length) return;
    
    isLoading = true;
    const startIdx = currentPostsCount;
    const endIdx = Math.min(currentPostsCount + postsPerPage, allPosts.length);
    const postsToAdd = allPosts.slice(startIdx, endIdx);

    // Remove sentinel if it exists
    const sentinel = feedContainer.querySelector('.scroll-sentinel');
    if (sentinel) {
        scrollObserver.unobserve(sentinel);
        sentinel.remove();
    }

    // Add new posts
    for (const post of postsToAdd) {
        const postElement = await createPostElement(post);
        feedContainer.appendChild(postElement);
    }

    currentPostsCount = endIdx;

    // Re-add sentinel if there are more posts
    if (endIdx < allPosts.length) {
        const newSentinel = document.createElement('div');
        newSentinel.className = 'scroll-sentinel';
        newSentinel.style.height = '1px';
        feedContainer.appendChild(newSentinel);
        scrollObserver.observe(newSentinel);
    }

    isLoading = false;
}

/**
 * Load verse text when article enters viewport (lazy loading)
 */
async function loadVerseForArticle(verseContainer) {
    if (verseContainer.dataset.loaded === 'true') return;

    const book = verseContainer.dataset.book;
    const chapter = parseInt(verseContainer.dataset.chapter);
    const verseStart = verseContainer.dataset.verseStart ? parseInt(verseContainer.dataset.verseStart) : null;
    const verseEnd = verseContainer.dataset.verseEnd ? parseInt(verseContainer.dataset.verseEnd) : null;

    if (!book || !chapter) return;

    try {
        // Viewer preference: check localStorage first, else Firestore
        let version = localStorage.getItem('user_preferred_lang') || null;
        if (!version) {
            try {
                version = await getUserLanguage();
            } catch (e) {}
        }
        version = version || 'french';

        // Fetch verse(s)
        let verseText = '';
        if (verseStart && (!verseEnd || verseEnd === verseStart)) {
            verseText = await getVerse(version, book, chapter, verseStart);
        } else if (verseStart && verseEnd) {
            const verses = await getVerses(version, book, chapter, verseStart, verseEnd);
            verseText = verses.map(v => v.text).join(' ');
        }

        if (verseText) {
            const node = document.createElement('div');
            node.className = 'verse-text-inline';
            node.innerHTML = `<div class="verse-text-ref">${escapeHtml(verseText)}</div>`;
            verseContainer.appendChild(node);
        }

        verseContainer.dataset.loaded = 'true';
    } catch (err) {
        console.debug('Verse load error:', err);
        verseContainer.dataset.loaded = 'true';
    }
}

/**
 * Create post element
 */
async function createPostElement(post) {
    const article = document.createElement('article');
    article.className = 'post-card';
    article.dataset.postId = post.id;

    const currentUser = getCurrentUser();
    const isAuthor = currentUser && currentUser.uid === post.authorId;

    // Format date
    const postDate = post.createdAt ? new Date(post.createdAt.toDate()).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }) : 'Date inconnue';

    // Handle repost: fetch original post and render embedded card
    if (post.type === 'repost' && post.originalPostId) {
        let originalPost = null;
        try {
            originalPost = await getPostById(post.originalPostId);
        } catch (e) {
            console.debug('Original post not found:', e);
        }

        const repostHeader = `
            <div class="repost-banner">
                🔄 Repartagé par <strong>${escapeHtml(post.author.displayName)}</strong>
                <span class="repost-date">&nbsp;· ${postDate}</span>
            </div>
        `;

        if (originalPost) {
            const origDate = originalPost.createdAt ? new Date(originalPost.createdAt.toDate()).toLocaleDateString('fr-FR', {
                year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
            }) : '';

            let origVerseRef = '';
            if (originalPost.book && originalPost.chapter) {
                origVerseRef = `<div class="verse-ref">📖 ${originalPost.book} ${originalPost.chapter}${originalPost.verseStart ? ':' + originalPost.verseStart : ''}${originalPost.verseEnd && originalPost.verseEnd !== originalPost.verseStart ? '-' + originalPost.verseEnd : ''}</div>`;
            }

            article.innerHTML = `
                ${repostHeader}
                <div class="repost-card">
                    <div class="post-header">
                        <img src="${originalPost.author.photoURL || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(originalPost.author.displayName)}"
                            alt="${escapeHtml(originalPost.author.displayName)}" class="author-avatar" />
                        <div class="author-info">
                            <div class="author-name">${escapeHtml(originalPost.author.displayName)}</div>
                            <div class="post-date">${origDate}</div>
                        </div>
                    </div>
                    ${origVerseRef}
                    <div class="post-content">${escapeHtml(originalPost.text)}</div>
                </div>
                <div id="reactions-container-${post.id}" class="reactions-container"></div>
                <div class="post-actions">
                    <button class="action-btn" data-action="comment">
                        <span class="icon">💬</span>
                        <span class="count">${post.commentsCount || 0}</span>
                    </button>
                    <button class="action-btn share-btn" data-action="share">
                        <span class="icon">🔗</span>
                        <span class="count">${post.sharesCount || 0}</span>
                    </button>
                </div>
            `;

            // Lazy verse loading for original post
            if (originalPost.book && originalPost.chapter) {
                const verseTextContainer = document.createElement('div');
                verseTextContainer.className = 'verse-text-container';
                verseTextContainer.dataset.loaded = 'false';
                verseTextContainer.dataset.book = originalPost.book;
                verseTextContainer.dataset.chapter = originalPost.chapter;
                verseTextContainer.dataset.verseStart = originalPost.verseStart || '';
                verseTextContainer.dataset.verseEnd = originalPost.verseEnd || '';
                const origVerseNode = article.querySelector('.repost-card .verse-ref');
                if (origVerseNode) {
                    origVerseNode.insertAdjacentElement('afterend', verseTextContainer);
                    if (verseObserver) verseObserver.observe(verseTextContainer);
                }
            }
        } else {
            // Original post deleted
            article.innerHTML = `
                ${repostHeader}
                <div class="repost-card repost-deleted">
                    <em>Cette publication a été supprimée.</em>
                </div>
                <div id="reactions-container-${post.id}" class="reactions-container"></div>
                <div class="post-actions">
                    <button class="action-btn" data-action="comment">
                        <span class="icon">💬</span>
                        <span class="count">${post.commentsCount || 0}</span>
                    </button>
                </div>
            `;
        }

        // Author controls for repost itself
        if (isAuthor) {
            const repostBanner = article.querySelector('.repost-banner');
            const deleteBtn = document.createElement('button');
            deleteBtn.className = 'delete-btn';
            deleteBtn.dataset.action = 'delete';
            deleteBtn.title = 'Supprimer le repost';
            deleteBtn.textContent = '🗑️';
            repostBanner.appendChild(deleteBtn);
        }

        attachPostEvents(article, post);

        const reactionsContainer = article.querySelector(`#reactions-container-${post.id}`);
        if (reactionsContainer) {
            const reactionsBar = new ReactionsBar(post.id);
            await reactionsBar.render(reactionsContainer, post);
        }

        return article;
    }

    // --- Standard post ---
    let verseRef = '';
    if (post.book && post.chapter) {
        verseRef = `
      <div class="verse-ref">
        📖 ${post.book} ${post.chapter}${post.verseStart ? ':' + post.verseStart : ''}${post.verseEnd && post.verseEnd !== post.verseStart ? '-' + post.verseEnd : ''}
      </div>
    `;
    }

    article.innerHTML = `
    <div class="post-header">
      <img
        src="${post.author.photoURL || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(post.author.displayName)}"
        alt="${escapeHtml(post.author.displayName)}"
        class="author-avatar"
      />
      <div class="author-info">
        <div class="author-name">${escapeHtml(post.author.displayName)}</div>
        <div class="post-date">${postDate}</div>
      </div>
      ${isAuthor ? `
        <div class="post-action-buttons">
          <button class="edit-post-btn" data-action="edit" title="Modifier">✏️</button>
          <button class="delete-btn" data-action="delete" title="Supprimer">🗑️</button>
        </div>
      ` : ''}
    </div>
    ${verseRef}
    <div class="post-content">
      ${escapeHtml(post.text)}
    </div>
    <div id="reactions-container-${post.id}" class="reactions-container"></div>
    <div class="post-actions">
      <button class="action-btn" data-action="comment">
        <span class="icon">💬</span>
        <span class="count">${post.commentsCount || 0}</span>
      </button>
      <button class="action-btn share-btn" data-action="share">
        <span class="icon">🔗</span>
        <span class="count">${post.sharesCount || 0}</span>
      </button>
    </div>
  `;

    attachPostEvents(article, post);

    // Add reactions bar
    const reactionsContainer = article.querySelector(`#reactions-container-${post.id}`);
    if (reactionsContainer) {
        const reactionsBar = new ReactionsBar(post.id);
        await reactionsBar.render(reactionsContainer, post);
    }

    // Lazy verse loading
    if (post.book && post.chapter) {
        const verseTextContainer = document.createElement('div');
        verseTextContainer.className = 'verse-text-container';
        verseTextContainer.dataset.loaded = 'false';
        verseTextContainer.dataset.book = post.book;
        verseTextContainer.dataset.chapter = post.chapter;
        verseTextContainer.dataset.verseStart = post.verseStart || '';
        verseTextContainer.dataset.verseEnd = post.verseEnd || '';

        const verseRefNode = article.querySelector('.verse-ref');
        if (verseRefNode) {
            verseRefNode.insertAdjacentElement('afterend', verseTextContainer);
            if (verseObserver) verseObserver.observe(verseTextContainer);
        }
    }

    return article;
}

/**
 * Attach event listeners to a post article element
 */
function attachPostEvents(article, post) {
    const commentBtn = article.querySelector('[data-action="comment"]');
    const deleteBtn = article.querySelector('[data-action="delete"]');
    const editBtn = article.querySelector('[data-action="edit"]');
    const shareBtn = article.querySelector('[data-action="share"]');

    if (commentBtn) commentBtn.addEventListener('click', () => handleViewComments(post.id, post));
    if (deleteBtn) deleteBtn.addEventListener('click', () => handleDelete(post.id));
    if (editBtn) editBtn.addEventListener('click', () => handleEditPost(post.id, post));
    if (shareBtn) shareBtn.addEventListener('click', (e) => handleShare(e, post));
}

/**
 * Handle share button click — show popover
 */
function handleShare(event, post) {
    // Close any existing popover
    document.querySelectorAll('.share-popover').forEach(p => p.remove());

    const btn = event.currentTarget;
    const popover = document.createElement('div');
    popover.className = 'share-popover';
    popover.innerHTML = `
        <button class="share-option" data-share="repost">🔄 Repartager sur le fil</button>
        <button class="share-option" data-share="copy">🔗 Copier le lien</button>
    `;

    btn.parentElement.style.position = 'relative';
    btn.parentElement.appendChild(popover);

    popover.querySelector('[data-share="repost"]').addEventListener('click', async () => {
        popover.remove();
        await handleRepost(post);
    });

    popover.querySelector('[data-share="copy"]').addEventListener('click', () => {
        popover.remove();
        handleCopyLink(post.id);
    });

    // Close on outside click
    const closeOnOutside = (e) => {
        if (!popover.contains(e.target) && e.target !== btn) {
            popover.remove();
            document.removeEventListener('click', closeOnOutside, true);
        }
    };
    setTimeout(() => document.addEventListener('click', closeOnOutside, true), 0);
}

/**
 * Handle repost action
 */
async function handleRepost(post) {
    const currentUser = getCurrentUser();
    if (!currentUser) {
        showToast('Connectez-vous pour repartager une publication.', 'error');
        return;
    }

    // Prevent reposting own post or a repost
    if (post.authorId === currentUser.uid) {
        showToast('Vous ne pouvez pas repartager votre propre publication.', 'error');
        return;
    }
    if (post.type === 'repost') {
        showToast('Impossible de repartager un repost.', 'error');
        return;
    }

    try {
        await repostPost(post.id, currentUser.uid);
        showToast('Publication repartagée sur votre fil !', 'success');
    } catch (error) {
        console.error('Repost error:', error);
        showToast('Erreur lors du repartage.', 'error');
    }
}

/**
 * Handle copy link action
 */
function handleCopyLink(postId) {
    const url = `${window.location.origin}${window.location.pathname}#post-${postId}`;
    navigator.clipboard.writeText(url).then(() => {
        showToast('Lien copié dans le presse-papier !', 'success');
    }).catch(() => {
        showToast('Impossible de copier le lien.', 'error');
    });
}

/**
 * Show a toast notification
 */
function showToast(message, type = 'success') {
    const existing = document.querySelector('.share-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = `share-toast share-toast--${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('share-toast--visible'));
    setTimeout(() => {
        toast.classList.remove('share-toast--visible');
        setTimeout(() => toast.remove(), 300);
    }, 3000);
}

/**
 * Handle view comments action
 */
function handleViewComments(postId, postData) {
    const modal = new CommentsModal(postId, postData);
    modal.open();
}

/**
 * Handle edit post action
 */
async function handleEditPost(postId, post) {
    const newText = prompt('Modifier votre publication:', post.text);
    if (newText && newText.trim() !== post.text) {
        try {
            const { updateDoc, doc } = await import('firebase/firestore');
            const { db } = await import('../firebase.js');
            
            const postRef = doc(db, 'posts', postId);
            await updateDoc(postRef, {
                text: newText.trim(),
                updatedAt: new Date()
            });
            
            console.log('✅ Publication modifiée');
            loadFeed(); // Reload feed
        } catch (error) {
            console.error('Erreur modification publication:', error);
            alert('Impossible de modifier la publication');
        }
    }
}

/**
 * Handle delete action
 */
async function handleDelete(postId) {
    // Modal de confirmation
    const confirmed = await showDeleteConfirm();
    if (!confirmed) return;

    try {
        const { deletePost } = await import('../db.js');
        await deletePost(postId);

        // Remove from DOM
        const postElement = document.querySelector(`[data-post-id="${postId}"]`);
        if (postElement) {
            postElement.style.transition = 'opacity 0.3s, transform 0.3s';
            postElement.style.opacity = '0';
            postElement.style.transform = 'scale(0.97)';
            setTimeout(() => postElement.remove(), 300);
        }
    } catch (error) {
        console.error('Error deleting post:', error);
        alert('Erreur lors de la suppression');
    }
}

/**
 * Show delete confirmation modal
 */
function showDeleteConfirm() {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'modal';
        overlay.innerHTML = `
            <div class="modal-content delete-confirm-modal">
                <div class="modal-header">
                    <h3>🗑️ Supprimer la publication</h3>
                </div>
                <div class="modal-body">
                    <p>Êtes-vous sûr de vouloir supprimer cette publication ? Cette action est irréversible.</p>
                    <div class="delete-confirm-actions">
                        <button class="btn btn-secondary" id="cancel-delete-btn">Annuler</button>
                        <button class="btn btn-danger" id="confirm-delete-btn">Supprimer</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);

        const close = (result) => {
            overlay.remove();
            resolve(result);
        };

        overlay.querySelector('#confirm-delete-btn').addEventListener('click', () => close(true));
        overlay.querySelector('#cancel-delete-btn').addEventListener('click', () => close(false));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) close(false); });
    });
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/\n/g, '<br>');
}
